/// <reference lib="dom" />
/* eslint-disable no-underscore-dangle */
import type { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'

import { getDidomiConfig } from './config'

/**
 * Function that creates the <script> element that will load the Didomi SDK
 * ⚠⚠ THIS FUNCTION SHOULD STAY SELF-CONTAINED ⚠⚠ (no reference to outside functions/modules) as it will be serialised into the SSR HTML
 * Function taken from Didomi and adapted (onerror handling) to our needs
 */
function loadDidomiSdk(apiKey: string, noticeId: string) {
  const preconnect = document.createElement('link')
  preconnect.rel = 'preconnect'
  preconnect.as = 'script'

  const dnsPrefetch = document.createElement('link')
  dnsPrefetch.rel = 'dns-prefetch'
  dnsPrefetch.as = 'script'

  const script = document.createElement('script')
  script.id = 'spcloader'
  script.type = 'text/javascript'
  script.async = true
  script.charset = 'utf-8'

  script.onerror = () => {
    // in case the event is emitted too early (before hydration), we set this flag so that late subscribers can still rely on it
    window.__didomiLoadError = true

    window.dispatchEvent(
      new CustomEvent('didomi:load-error', {
        detail: { message: 'Failed to load Didomi SDK' },
      }),
    )
  }

  let src = `https://sdk.privacy-center.org/${apiKey}/loader.js?target_type=notice&target=${noticeId}`

  if (window.didomiConfig?.user) {
    const { country, region } = window.didomiConfig.user
    if (country) {
      src = `${src}&country=${country}`
      if (region) {
        src = `${src}&region=${region}`
      }
    }
  }

  preconnect.href = 'https://sdk.privacy-center.org/'
  dnsPrefetch.href = 'https://sdk.privacy-center.org/'
  script.src = src

  const firstScript = document.getElementsByTagName('script')[0]
  const parent = firstScript.parentNode
  if (!parent) return

  parent.insertBefore(preconnect, firstScript)
  parent.insertBefore(dnsPrefetch, firstScript)
  parent.insertBefore(script, firstScript)
}

function getDidomiScriptContent({
  apiKey,
  noticeId,
  countryCode,
}: {
  apiKey: string
  noticeId: string
  countryCode: MarketCountryCode
}) {
  return `window.didomiConfig = ${JSON.stringify(getDidomiConfig(countryCode))};
    (${loadDidomiSdk.toString()})(${JSON.stringify(apiKey)}, ${JSON.stringify(noticeId)});`
}

export function getDidomiScript({
  apiKey,
  noticeId,
  countryCode,
}: {
  apiKey: string
  noticeId: string
  countryCode: MarketCountryCode
}) {
  return `
  <script type="text/javascript">
    ${getDidomiScriptContent({ apiKey, noticeId, countryCode })}
    </script>`
}

export function createDidomiScriptElement({
  apiKey,
  noticeId,
  countryCode,
}: {
  apiKey: string
  noticeId: string
  countryCode: MarketCountryCode
}) {
  const innerHTML = getDidomiScriptContent({ apiKey, noticeId, countryCode })

  const scriptElement = document.createElement('script')
  // nosemgrep: javascript.browser.security.insecure-document-method.insecure-document-method
  scriptElement.innerHTML = innerHTML

  return scriptElement
}
